// @flow
import React from 'react'
import AppBar from '@material-ui/core/AppBar'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import ResizeObserver from 'resize-observer-polyfill'
import { windowStates } from './window_state'
import ErrorBoundary from './error_boundary'
import type { TabbedPanelType } from '../reducers/tabbed_panel'

type PropsType = {
    children: any[],
    setTabKey?: string,
    setTabKeyHandled?: Function,
    maptoolbarVisible: boolean,
    onSetTabbedPanel?: Function,
    onSetTabbedPanelIndex?: Function,
    scrollable?: boolean,
    theme: {},
    tocVisible: boolean,
    tabbedPanel: TabbedPanelType,
}

type StateType = {
    height: number,
    tabindex: number,
}

class TabbedPanel extends React.PureComponent<PropsType, StateType> {
    static defaultProps = {
        onSetTabbedPanel: (t) => {},
        onSetTabbedPanelIndex: (t) => {},
        setTabKey: null,
        setTabKeyHandled: (t) => {},
        scrollable: true,
        tabindex: undefined,
    }

    constructor(props) {
        super(props)
        this.state = {
            tabindex: 0,
        }
    }

    componentDidMount() {
        this.addResizeEvent()
    }

    componentWillReceiveProps(nextProps: PropsType) {
        const oldChildren = this.getChildren(this.props)
        const newChildren = this.getChildren(nextProps)

        // Look for new panels
        const oldkeys = oldChildren.map(c => this.getTabKey(c))
        const newPanels = newChildren.filter(c => oldkeys.indexOf(this.getTabKey(c)) < 0)
        let newtabindex = nextProps.tabbedPanel.tabindex || this.state.tabindex
        // Check if tab is being programmatically set.
        if (nextProps.setTabKey) {
            newtabindex = newChildren.findIndex(c => this.getTabKey(c) === nextProps.setTabKey)
            this.props.setTabKeyHandled()
        } else if (newPanels.length) {
            // New panels found. Select the first one.
            newtabindex = newChildren.findIndex(c => this.getTabKey(c) === this.getTabKey(newPanels[0]))
        }
        // Check that the current tab index is still valid
        if (newtabindex < 0 || newtabindex >= newChildren.length) newtabindex = 0
        else if (newtabindex >= 0 && newChildren.length && newChildren[newtabindex].props.disabled) {
            // Set to first available tab
            const indexes = newChildren.map((c, i) => (c.props.disabled ? -1 : i)).filter(i => i >= 0)
            if (indexes.length) newtabindex = indexes[0]
        }
        if (newtabindex >= 0 && this.state.tabindex !== newtabindex) {
            this.setState({ tabindex: newtabindex })
        }
    }

    getTabKey(child) {
        return child.key.substr(2) /* strip off leading '.$' */
    }

    getChildren(props) {
        return React.Children.toArray(props.children).filter(c => c)
    }

    addResizeEvent() {
        const handleResize = () => {
            if (this.container) {
                this.getHeight()
            }
        }

        // https://gi2thub.com/que-etc/resize-observer-polyfill
        this.ro = new ResizeObserver(() => {
            handleResize()
        })
        this.ro.observe(this.container)
        handleResize()
    }

    getHeight() {
        const container = this.container.closest('.resizableTableContainer')
        const header = container.getElementsByTagName('header')
        if (header.length) {
            const appBarHeight = header[0].getBoundingClientRect().height
            const fullHeight = this.container.getBoundingClientRect().height
            const height = fullHeight - appBarHeight
            this.setState({ height })
        }
    }

    onChangeTab = (e, v) => {
        this.setState({ tabindex: v })
        this.props.onSetTabbedPanelIndex(v)
        const children = this.getChildren(this.props)
        this.props.onSetTabbedPanel(this.getTabKey(children[v]))
    }

    render() {
        const rootClassNames = [this.props.theme.analysisPanel]
        rootClassNames.push(this.props.tocVisible ? this.props.theme.contextBarOpen : this.props.theme.contextBarClosed)
        rootClassNames.push(this.props.maptoolbarVisible ? this.props.theme.mapToolbarPanelOpen : this.props.theme.mapToolbarPanelClosed)
        if (this.props.tabbedPanel.windowState === windowStates.maximized) rootClassNames.push(this.props.theme.headerPanelOpen)
        const classes = rootClassNames.join(' ')

        const st = {}
        if (this.props.tabbedPanel.windowState === windowStates.minimized) {
            st.display = 'none'
        }

        const children = this.getChildren(this.props)
        return (
            <ErrorBoundary><div
                className={`resizableTableContainer ${classes}`}
                ref={(r) => {
                    this.container = r
                }}
                style={st}
            >
                {children.length > 0 && (<AppBar
                    position="static"
                >
                    <Tabs
                        variant={this.props.scrollable ? 'scrollable' : 'standard'}
                        value={this.state.tabindex}
                        onChange={this.onChangeTab}
                    >
                        {children.map((c) => {
                            // Look for a list of buttons
                            const labelWithButtons = []
                            const tabLabel = this.getTabKey(c)
                            if (c.props.buttons) {
                                labelWithButtons.push(
                                    (<React.Fragment
                                        key={this.getTabKey(c)}
                                    >
                                        <span>{tabLabel}</span>
                                        {c.props.buttons.map(b => (
                                            <React.Fragment
                                                key={b.className}
                                            >
                                                <span style={{ display: 'inline-block', marginLeft: '2px' }}>
                                                    <i
                                                        className={`${this.props.theme.tabbedPanelLabelButton} ${b.className}`}
                                                        tabIndex={-1}
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            e.stopPropagation()
                                                            b.event(e)
                                                        }}
                                                        role="button"
                                                        style={b.style}
                                                    />
                                                </span>
                                            </React.Fragment>
                                        ))}
                                    </React.Fragment>),
                                )
                            }
                            return (
                                <Tab
                                    disabled={c.props.disabled}
                                    key={this.getTabKey(c)}
                                    label={labelWithButtons.length ?
                                        labelWithButtons
                                        : tabLabel}
                                />
                            )
                        })}
                    </Tabs>
                </AppBar>)}
                {children.map((C, i) => (
                    <div
                        className={'tabbedPanel'}
                        key={this.getTabKey(C)}
                        style={{ display: this.state.tabindex === i ? 'block' : 'none' }}
                    >
                        {<C.type {...C.props} active={this.state.tabindex === i} height={this.state.height} />}
                    </div>
                ))}
            </div></ErrorBoundary>
        )
    }
}

export default TabbedPanel
